import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Alert, Image, Row, Col, Spinner } from 'react-bootstrap';

import { useUserApi } from '@/shared/services/userApi';
import { useAuth } from '@/shared/contexts/AuthContext';

// Default avatar placeholder (inline SVG data URL)
// prettier-ignore
const DEFAULT_AVATAR = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120"%3E%3Ccircle cx="60" cy="60" r="60" fill="%23e9ecef"/%3E%3Ccircle cx="60" cy="45" r="20" fill="%23adb5bd"/%3E%3Cellipse cx="60" cy="95" rx="35" ry="25" fill="%23adb5bd"/%3E%3C/svg%3E'; // eslint-disable-line max-len

export default function BasicProfileForm({ profile, onUpdate }) {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [bio, setBio] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);

    // Profile picture state
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePicturePreview, setProfilePicturePreview] = useState(null);
    const [pictureError, setPictureError] = useState('');

    const userApi = useUserApi();
    const { refreshProfileStatus } = useAuth();

    useEffect(() => {
        if (profile) {
            setFirstName(profile.name?.first || '');
            setLastName(profile.name?.last || '');
            setBio(profile.bio || '');
        }
    }, [profile]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/gif',
        ];
        if (!allowedTypes.includes(file.type)) {
            setPictureError('Please select a JPEG, PNG, or GIF image.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setPictureError('File size must be less than 5MB.');
            return;
        }

        setPictureError('');
        setProfilePicture(file);
        setProfilePicturePreview(URL.createObjectURL(file));
    };

    const handleRemovePicture = () => {
        setProfilePicture(null);
        setProfilePicturePreview(null);
        setPictureError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSaving(true);

        try {
            await userApi.updateProfile({
                name: { first: firstName, last: lastName },
                bio,
            });

            if (profilePicture) {
                try {
                    const formData = new FormData();
                    formData.append('profilePicture', profilePicture);
                    await userApi.uploadProfilePicture(formData);
                } catch (uploadError) {
                    console.error(
                        'Profile picture upload failed:',
                        uploadError
                    );
                }
            }

            setSuccess('Profile updated successfully!');
            setProfilePicture(null);
            setProfilePicturePreview(null);
            refreshProfileStatus();
            if (onUpdate) onUpdate();
        } catch (err) {
            setError('Failed to update profile. Please try again.');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            {error && <Alert variant="danger">{error}</Alert>}
            {success && (
                <Alert
                    variant="success"
                    dismissible
                    onClose={() => setSuccess('')}
                >
                    {success}
                </Alert>
            )}

            {/* Profile Picture */}
            <Form.Group className="mb-4 text-center">
                <div className="mb-3">
                    <Image
                        src={
                            profilePicturePreview ||
                            profile?.profilePicture ||
                            DEFAULT_AVATAR
                        }
                        alt="Profile preview"
                        roundedCircle
                        className="profile-avatar-preview"
                    />
                </div>

                <div className="d-flex justify-content-center gap-2">
                    <Form.Control
                        type="file"
                        accept=".jpg,.jpeg,.png,.gif"
                        onChange={handleFileChange}
                        disabled={saving}
                        className="d-none"
                        id="profile-picture-input"
                    />
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="rounded-pill"
                        onClick={() =>
                            document
                                .getElementById('profile-picture-input')
                                .click()
                        }
                        disabled={saving}
                    >
                        {profilePicture || profile?.profilePicture
                            ? 'Change Photo'
                            : 'Upload Photo'}
                    </Button>
                    {profilePicture && (
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="rounded-pill"
                            onClick={handleRemovePicture}
                            disabled={saving}
                        >
                            Remove
                        </Button>
                    )}
                </div>

                {pictureError && (
                    <div className="text-danger small mt-2">{pictureError}</div>
                )}

                <Form.Text className="text-muted d-block mt-1">
                    JPEG, PNG, or GIF (max 5MB)
                </Form.Text>
            </Form.Group>

            <p className="text-center text-muted small mb-4">
                {profile?.email}
            </p>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="First name"
                            required
                            disabled={saving}
                        />
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Last name"
                            required
                            disabled={saving}
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-4">
                <Form.Label>Bio</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell us about yourself..."
                    disabled={saving}
                />
            </Form.Group>

            <div className="d-grid">
                <Button
                    type="submit"
                    variant="primary"
                    className="rounded-pill"
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <Spinner
                                animation="border"
                                size="sm"
                                className="me-2"
                            />
                            Saving...
                        </>
                    ) : (
                        'Save Profile'
                    )}
                </Button>
            </div>
        </Form>
    );
}

BasicProfileForm.propTypes = {
    profile: PropTypes.shape({
        name: PropTypes.shape({
            first: PropTypes.string,
            last: PropTypes.string,
        }),
        bio: PropTypes.string,
        email: PropTypes.string,
        profilePicture: PropTypes.string,
    }),
    onUpdate: PropTypes.func,
};
